import {
  TCutbankInActivityLogPage,
  TCutbankInTransactionLogPage,
  TRESPONSE_CUTBANK_IN_ACTIVITY_LOG,
  TRESPONSE_CUTBANK_OUT_ACTIVITY_LOG,
} from '@monorepo/types';
import { TTableOptions, TTableTreeModel } from '@monorepo/ui';
import moment from 'moment';
import { ModifiersZTableModel } from '../../../table.modifier';
import { TTableCellTree } from '../../../table.type';

const getValue = (
  header: TCutbankInActivityLogPage['table_config']['table_main_headers'][number],
  item: TRESPONSE_CUTBANK_IN_ACTIVITY_LOG['logs'][number],
  status: TRESPONSE_CUTBANK_IN_ACTIVITY_LOG['logs'][number]['transaction_type'],
): string | number => {
  const keyToCheck: 'old' | 'new' = status === 'delete' ? 'old' : 'new';

  if (header.key === 'datetime') {
    return moment(item.datetime).format('DD MMMM') + ', ' + moment(item.datetime).format('hh:mm A');
  } else if (header.key === 'actual_qty') {
    return item.data[keyToCheck]?.actual_qty as number;
  } else if (header.key === 'event_type') {
    return status === 'new' ? 'added' : status === 'edit' ? 'edited' : 'deleted';
  } else {
    return item.data[keyToCheck]?.details[header.key] as string;
  }
};

const getHeaderConfig = (tableConfig: TCutbankInTransactionLogPage['table_config'], isTabletScreen = false) => {
  const mainHeaders = tableConfig.table_main_headers.map((head, index) => {
    return {
      type: 'TEXT_CELL' as const,
      text: head.display_name,
      align: 'left' as const,
      ...(!isTabletScreen && {
        stickyType: 'LEFT',
        stickyPosition: index + 1,
      }),

      widthInPx: 120,
      maxWidthInPx: 120,
      ...(index === tableConfig.table_main_headers.length - 1 && { borderRight: true }),
    };
  });

  const secondaryHeaders = tableConfig.collapsible_list_config.table_in_row_column.map((head) => {
    return {
      type: 'TEXT_CELL' as const,
      align: 'left' as const,

      text: head.display_name,
    };
  });

  return [...mainHeaders, ...secondaryHeaders];
};

const getRows = (
  item: TRESPONSE_CUTBANK_IN_ACTIVITY_LOG['logs'][number],
  tableConfig: TCutbankInActivityLogPage['table_config'],
) => {
  const mainRows = tableConfig.table_main_headers.map((head) => {
    const value = getValue(head, item, item.transaction_type);
    return {
      type: 'TEXT_CELL',
      text: value,
      align: 'left',
    };
  });

  const secondaryRows = tableConfig.collapsible_list_config.table_in_row_column.map((head) => {
    const value = getValue(head, item, item.transaction_type);
    return {
      type: 'TEXT_CELL',
      text: value,
      align: 'left',
    };
  });

  return [...mainRows, ...secondaryRows];
};

export const CUTBANK_ACTIVITY_LOG_TABLE = {
  createModel: (
    items: TRESPONSE_CUTBANK_IN_ACTIVITY_LOG['logs'] | TRESPONSE_CUTBANK_OUT_ACTIVITY_LOG['logs'],
    tableConfig: TCutbankInActivityLogPage['table_config'],
    isTabletScreen: boolean = false,
  ) => {
    return ModifiersZTableModel.createTableModel(
      items,
      {
        headerConfig: getHeaderConfig(tableConfig, isTabletScreen) as TTableOptions['headerConfig'],
      },
      (item) => {
        return {
          type: 'ROW' as const,
          rowItem: item,
          cells: getRows(item, tableConfig) as TTableCellTree[],
        };
      },
    );
  },

  emptyModel: (
    items: TRESPONSE_CUTBANK_IN_ACTIVITY_LOG['logs'],
    tableConfig: TCutbankInActivityLogPage['table_config'],
  ) => {
    return ModifiersZTableModel.createTableModel(
      items,
      {
        headerConfig: tableConfig.table_main_headers.map((head, index) => {
          return {
            type: 'TEXT_CELL' as const,
            text: head.display_name,
          };
        }),
      },
      (item) => {
        return {
          type: 'ROW' as const,
          rowItem: item,
          cells: tableConfig.table_main_headers.map((head) => {
            const value = getValue(head, item, item.transaction_type);
            return {
              type: 'TEXT_CELL' as const,
              text: value,
            };
          }),
        };
      },
    );
  },
};

export type TCutbankInActivityLogTableType = TTableTreeModel<TRESPONSE_CUTBANK_IN_ACTIVITY_LOG['logs'][number]>;
